
import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { useUserFetch } from '@/hooks/useUserFetch';
import { useUserManagement } from '@/hooks/useUserManagement';
import { useRolePermissions } from '@/hooks/useRolePermissions';
import { User } from '@/types/user';
import { Loader2, Trash, Shield, ShieldAlert } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/contexts/LanguageContext';

export const UserTable: React.FC = () => {
  const { users, isLoading, error } = useUserFetch();
  const { deleteUser, toggleAdminStatus } = useUserManagement();
  const { isAdminOrHigher, isSuper, userRole } = useRolePermissions();
  const { direction } = useLanguage();

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return <div className="text-destructive py-4 text-center">שגיאה בטעינת משתמשים: {error.message}</div>;
  }

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'super':
        return <Badge variant="outline" className="bg-blue-50 text-blue-800 hover:bg-blue-50">מנהל מערכת</Badge>;
      case 'admin':
        return <Badge variant="outline" className="bg-green-50 text-green-800 hover:bg-green-50">מנהל</Badge>;
      case 'member':
        return <Badge variant="outline" className="bg-purple-50 text-purple-800 hover:bg-purple-50">חבר</Badge>;
      case 'viewer':
        return <Badge variant="outline" className="bg-gray-50 text-gray-800 hover:bg-gray-50">צופה</Badge>;
      default:
        return <Badge variant="outline">לא ידוע</Badge>;
    }
  };

  const canManageUser = (user: User) => {
    // Super users can manage anyone except other super users
    if (isSuper) {
      return user.role !== 'super' || user.id === (userRole as any)?.user_id;
    }
    
    // Admins can manage members and viewers
    if (userRole?.role === 'admin') {
      return user.role === 'member' || user.role === 'viewer';
    }
    
    return false;
  };

  return (
    <div className="rounded-md border" dir={direction}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className={direction === 'rtl' ? 'text-right' : 'text-left'}>דוא"ל</TableHead>
            <TableHead className={direction === 'rtl' ? 'text-right' : 'text-left'}>תפקיד</TableHead>
            <TableHead className={direction === 'rtl' ? 'text-right' : 'text-left'}>סטטוס</TableHead>
            <TableHead className={direction === 'rtl' ? 'text-left' : 'text-right'}>פעולות</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                לא נמצאו משתמשים
              </TableCell>
            </TableRow>
          ) : (
            users.map((user) => (
              <TableRow key={user.id}>
                <TableCell className={direction === 'rtl' ? 'text-right' : 'text-left'}>{user.email}</TableCell>
                <TableCell className={direction === 'rtl' ? 'text-right' : 'text-left'}>{getRoleBadge(user.role)}</TableCell>
                <TableCell className={direction === 'rtl' ? 'text-right' : 'text-left'}>
                  {user.is_active ? (
                    <Badge variant="outline" className="bg-green-50 text-green-800 hover:bg-green-50">פעיל</Badge>
                  ) : (
                    <Badge variant="outline" className="bg-red-50 text-red-800 hover:bg-red-50">לא פעיל</Badge>
                  )}
                </TableCell>
                <TableCell className={`${direction === 'rtl' ? 'text-left' : 'text-right'} space-x-2 ${direction === 'rtl' ? 'space-x-reverse' : ''}`}>
                  {canManageUser(user) && (
                    <>
                      {isSuper && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleAdminStatus(user.id, user.role === 'admin')}
                          className="ml-2"
                        >
                          {user.role === 'admin' ? (
                            <>
                              <ShieldAlert className={`h-4 w-4 ${direction === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                              <span>הסר הרשאת מנהל</span>
                            </>
                          ) : (
                            <>
                              <Shield className={`h-4 w-4 ${direction === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                              <span>הפוך למנהל</span>
                            </>
                          )}
                        </Button>
                      )}
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => deleteUser(user.id)}
                        className={`${direction === 'rtl' ? 'mr-2' : 'ml-2'}`}
                      >
                        <Trash className={`h-4 w-4 ${direction === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                        <span>מחק</span>
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};
